import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function ContentManagement() {
  const [title, setTitle] = React.useState("");
  const [contentType, setContentType] = React.useState("video");
  const [contentStatus, setContentStatus] = React.useState("published");
  const [category, setCategory] = React.useState("Getting Started");
  const [contentBody, setContentBody] = React.useState("");
  const [uploadedFileName, setUploadedFileName] = React.useState("");
  const [uploadedFile, setUploadedFile] = React.useState(null);
  const [editingId, setEditingId] = React.useState("");
  const [showAddPanel, setShowAddPanel] = React.useState(false);
  const [filterType, setFilterType] = React.useState("all");
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [searchTerm] = React.useState("");
  const [page] = React.useState(1);
  const [limit] = React.useState(10);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [actionAnchorEl, setActionAnchorEl] = React.useState(null);
  const [selectedActionItem, setSelectedActionItem] = React.useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = React.useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [previewItem, setPreviewItem] = React.useState(null);
  const [selectedContentIds, setSelectedContentIds] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [totalContents, setTotalContents] = React.useState(0);
  const [totalVideos, setTotalVideos] = React.useState(0);
  const [formError, setFormError] = React.useState("");
  const MAX_VIDEOS = 4;

  const columns = React.useMemo(
    () => [
      { Header: "select", accessor: "select", align: "center" },
      { Header: "title", accessor: "title", align: "left" },
      { Header: "category", accessor: "category", align: "left" },
      { Header: "type", accessor: "type", align: "center" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "updated", accessor: "updated", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ],
    []
  );

  const formatDate = () =>
    new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const resetForm = () => {
    setTitle("");
    setContentType("video");
    setContentStatus("published");
    setCategory("Getting Started");
    setContentBody("");
    setUploadedFileName("");
    setUploadedFile(null);
    setEditingId("");
    setFormError("");
    setShowAddPanel(false);
  };

  React.useEffect(() => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");

    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}/contents`);

    const params = { page, limit };
    if (filterType !== "all") params.type = filterType;
    if (filterStatus !== "all") params.status = filterStatus;
    if (searchTerm.trim()) params.search = searchTerm.trim();

    let isMounted = true;
    const fetchContents = async () => {
      for (const url of requestUrls) {
        try {
          const { data } = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params,
          });
          if (!isMounted) return;
          const payload = data?.data || data || {};
          const list = payload.contents || payload.docs || payload.items || payload.results || [];
          const mappedItems = list.map((item) => ({
            id: item._id || item.id,
            title: item.title || "-",
            type: String(item.type || "text").toLowerCase(),
            category: item.category || "-",
            body: item.text || item.videoUrl || item.body || "",
            status: String(item.status || "draft").toLowerCase(),
            updated: item.updatedAt
              ? new Date(item.updatedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : formatDate(),
          }));
          const resolvedTotal =
            payload.totalContents ??
            payload.total ??
            payload.pagination?.total ??
            payload.meta?.total ??
            mappedItems.length;
          setItems(mappedItems);
          setTotalContents(resolvedTotal);
          if (filterType === "video") {
            setTotalVideos(Number(resolvedTotal) || 0);
          } else if (filterType === "all") {
            setTotalVideos(mappedItems.filter((item) => item.type === "video").length);
          }
          return;
        } catch (_error) {
          // Try next URL shape.
        }
      }
      if (isMounted) {
        setItems([]);
        setTotalContents(0);
        if (filterType === "video" || filterType === "all") setTotalVideos(0);
      }
    };

    const fetchVideoCount = async () => {
      if (filterType === "video") return;
      for (const url of requestUrls) {
        try {
          const { data } = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params: { page: 1, limit: 1, type: "video" },
          });
          if (!isMounted) return;
          const payload = data?.data || data || {};
          const resolvedTotal =
            payload.totalContents ??
            payload.total ??
            payload.pagination?.total ??
            payload.meta?.total ??
            (payload.contents || payload.docs || payload.items || payload.results || []).length;
          setTotalVideos(Number(resolvedTotal) || 0);
          return;
        } catch (_error) {
          // Try next URL shape.
        }
      }
    };

    fetchContents();
    fetchVideoCount();
    return () => {
      isMounted = false;
    };
  }, [filterStatus, filterType, limit, page, reloadKey, searchTerm]);

  const rows = React.useMemo(
    () =>
      items.map((i) => ({
        select: (
          <MDBox display="flex" justifyContent="center">
            <Checkbox
              checked={selectedContentIds.includes(i.id)}
              onChange={(event) => {
                const checked = event.target.checked;
                setSelectedContentIds((prev) => {
                  if (checked) return [...new Set([...prev, i.id])];
                  return prev.filter((id) => id !== i.id);
                });
              }}
              size="small"
            />
          </MDBox>
        ),
        title: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="info"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleOpenPreview(i.id)}
          >
            {i.title}
          </MDTypography>
        ),
        category: (
          <MDTypography variant="caption" color="text">
            {i.category}
          </MDTypography>
        ),
        type: (
          <MDBox display="flex" justifyContent="center">
            <MDBadge
              badgeContent={i.type === "video" ? "Video" : "Text"}
              color={i.type === "video" ? "info" : "dark"}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        status: (
          <MDBox display="flex" justifyContent="center">
            <MDBadge
              badgeContent={i.status}
              color={
                i.status === "published" ? "success" : i.status === "archived" ? "error" : "warning"
              }
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        updated: (
          <MDTypography variant="caption" color="text">
            {i.updated}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center">
            <IconButton
              size="small"
              onClick={(event) => {
                setActionAnchorEl(event.currentTarget);
                setSelectedActionItem(i);
              }}
              sx={{ color: "#344767" }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </MDBox>
        ),
      })),
    [items, selectedContentIds]
  );

  const allVisibleSelected =
    items.length > 0 && items.every((item) => selectedContentIds.includes(item.id));

  React.useEffect(() => {
    setSelectedContentIds((prev) => prev.filter((id) => items.some((item) => item.id === id)));
  }, [items]);

  const fetchContentByIdApi = React.useCallback(async (id) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/contents/${id}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        const { data } = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return data?.data || data || null;
      } catch (_error) {
        // Try next URL shape.
      }
    }
    return null;
  }, []);

  const resolveVideoSource = React.useCallback((source) => {
    const normalizeMediaUrl = (value) => {
      const raw = typeof value === "string" ? value.trim() : "";
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;

      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      let origin = "";
      try {
        origin = new URL(apiBaseUrl).origin;
      } catch (_error) {
        origin = "";
      }
      if (!origin) return raw;
      const path = raw.startsWith("/") ? raw : `/${raw}`;
      return `${origin}${path}`;
    };

    if (!source || typeof source !== "object") return "";
    const candidates = [
      source.videoUrl,
      source.mediaUrl,
      source.fileUrl,
      source.url,
      source.video,
      source.videoFile,
      source.file,
      source.body,
      source.text,
      source.data?.videoUrl,
      source.data?.mediaUrl,
      source.data?.fileUrl,
      source.data?.url,
      source.data?.videoFile,
    ];
    const resolved = candidates.find((value) => typeof value === "string" && value.trim());
    return normalizeMediaUrl(resolved);
  }, []);

  const handleOpenPreview = React.useCallback(
    async (id) => {
      setShowPreviewDialog(true);
      setIsPreviewLoading(true);
      setPreviewItem(null);

      const data = await fetchContentByIdApi(id);
      const source = data?.content || data?.item || data || {};
      setPreviewItem({
        id: source._id || source.id || id,
        title: source.title || "-",
        type: String(source.type || "text").toLowerCase(),
        status: String(source.status || "draft").toLowerCase(),
        category: source.category || "-",
        text: source.text || source.body || "",
        videoUrl: resolveVideoSource(source),
      });
      setIsPreviewLoading(false);
    },
    [fetchContentByIdApi, resolveVideoSource]
  );

  const handleSaveContent = async () => {
    const cleanTitle = title.trim();
    const hasVideoFile = Boolean(uploadedFile);
    const textPayload = contentBody.trim();

    setFormError("");
    if (!cleanTitle) return;
    if (contentType === "text" && !textPayload) return;
    if (contentType === "video" && !hasVideoFile && !editingId) return;

    if (contentType === "video" && totalVideos >= MAX_VIDEOS) {
      const existingItem = items.find((item) => item.id === editingId);
      const wasAlreadyVideo = existingItem?.type === "video";
      if (!editingId || !wasAlreadyVideo) {
        setFormError(
          `Maximum ${MAX_VIDEOS} videos allowed. Delete an existing video to add a new one.`
        );
        return;
      }
    }

    if (editingId) {
      const existingItem = items.find((item) => item.id === editingId);
      await updateContentApi({
        id: editingId,
        title: cleanTitle,
        type: contentType,
        category,
        status: contentStatus || existingItem?.status || "published",
        text: contentType === "text" ? textPayload : "",
        file: contentType === "video" ? uploadedFile : null,
      });
      setReloadKey((prev) => prev + 1);
      resetForm();
      return;
    }

    await createContentApi({
      title: cleanTitle,
      type: contentType,
      category,
      text: contentType === "text" ? textPayload : "",
      status: contentStatus || "archived",
      file: contentType === "video" ? uploadedFile : null,
    });

    setItems((prev) => [
      {
        id: `c-${Date.now()}`,
        title: cleanTitle,
        type: contentType,
        category,
        body: contentType === "text" ? textPayload : uploadedFileName.trim(),
        status: "draft",
        updated: formatDate(),
      },
      ...prev,
    ]);
    setReloadKey((prev) => prev + 1);
    resetForm();
  };

  const updateContentApi = React.useCallback(
    async ({
      id,
      title: nextTitle,
      type,
      category: nextCategory,
      status,
      text,
      mediaUrl,
      file,
    }) => {
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const endpoint = `/contents/${id}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      for (const url of requestUrls) {
        try {
          if (type === "video") {
            const formData = new FormData();
            formData.append("title", nextTitle);
            formData.append("type", type);
            formData.append("status", status || "published");
            formData.append("category", nextCategory);
            if (mediaUrl) formData.append("videoUrl", mediaUrl);
            if (file) formData.append("file", file);

            await axios.patch(url, formData, {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            });
          } else {
            await axios.patch(
              url,
              {
                title: nextTitle,
                status: status || "published",
                type,
                category: nextCategory,
                text,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              }
            );
          }
          return;
        } catch (_requestError) {
          // Try next URL shape.
        }
      }
    },
    []
  );

  const createContentApi = React.useCallback(
    async ({ title: nextTitle, type, category: nextCategory, text, status, mediaUrl, file }) => {
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const endpoint = "/contents";
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      for (const url of requestUrls) {
        try {
          if (type === "video") {
            const formData = new FormData();
            formData.append("title", nextTitle);
            formData.append("type", type);
            formData.append("status", status || "archived");
            formData.append("category", nextCategory);
            if (mediaUrl) formData.append("videoUrl", mediaUrl);
            if (file) formData.append("file", file);

            await axios.post(url, formData, {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            });
          } else {
            await axios.post(
              url,
              {
                title: nextTitle,
                status: status || "archived",
                type,
                category: nextCategory,
                text,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              }
            );
          }
          return;
        } catch (_requestError) {
          // Try next URL shape.
        }
      }
    },
    []
  );

  const deleteContentApi = React.useCallback(async (id) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/contents/${id}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    for (const url of requestUrls) {
      try {
        await axios.delete(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return;
      } catch (_error) {
        // Try next URL shape.
      }
    }
  }, []);

  const bulkDeleteContentsApi = React.useCallback(async (ids) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = "/contents/bulk-delete";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    for (const url of requestUrls) {
      try {
        await axios.delete(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          data: { contentIds: ids },
        });
        return;
      } catch (_error) {
        // Try next URL shape.
      }
    }
  }, []);

  const moveContentApi = React.useCallback(async (id, direction) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/contents/${id}/${direction === "up" ? "move-up" : "move-down"}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    for (const url of requestUrls) {
      try {
        await axios.patch(
          url,
          {},
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        return;
      } catch (_error) {
        // Try next URL shape.
      }
    }
  }, []);

  const patchContentStatusApi = React.useCallback(async (id, nextStatus) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/contents/${id}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        await axios.patch(
          url,
          { status: nextStatus },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        return;
      } catch (_error) {
        // Try next URL shape.
      }
    }
  }, []);

  const downloadAdminGuide = () => {
    const guide = [
      "DSF Admin Quick Guide",
      "",
      "1) Users: Open Users page, select one or multiple users, and run bulk report actions.",
      "2) Tags: Create and update tags to group users for filters and reports.",
      "3) Subscriptions: Resolve billing, adjust plan duration, and convert trial users to paid.",
      "4) Reports: Download individual and grouped reports (CSV/TXT).",
      "5) Content: Add onboarding videos/text and reorder content by moving items up or down.",
      "",
      "Tip: Use filters on every page for faster navigation.",
    ].join("\n");

    const blob = new Blob([guide], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dsf-admin-quick-guide.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={0}
                py={3}
                px={2}
                mb={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  Content Management (Onboarding Videos & Texts)
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <MDBox mb={2}>
                  <MDTypography variant="button" color="text">
                    Easy content panel for uploading, organizing, and updating onboarding videos and
                    instructional texts without technical work. Maximum {MAX_VIDEOS} videos allowed
                    ({totalVideos}/{MAX_VIDEOS} used).
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="flex-end" mb={2}>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton
                      variant="outlined"
                      color="error"
                      disabled={selectedContentIds.length === 0}
                      onClick={async () => {
                        if (selectedContentIds.length === 0) return;
                        if (!window.confirm("Do you want to delete selected content?")) return;
                        await bulkDeleteContentsApi(selectedContentIds);
                        setSelectedContentIds([]);
                        setReloadKey((prev) => prev + 1);
                      }}
                    >
                      Bulk Delete ({selectedContentIds.length})
                    </MDButton>
                    <MDButton variant="outlined" color="dark" onClick={downloadAdminGuide}>
                      Download Admin Guide
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() => {
                        setFormError("");
                        if (totalVideos >= MAX_VIDEOS) {
                          setContentType("text");
                        }
                        setShowAddPanel(true);
                      }}
                      sx={{
                        background: "rgba(229, 10, 70, 1)",
                        "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                      }}
                    >
                      Add Content
                    </MDButton>
                  </MDBox>
                </MDBox>

                <Divider sx={{ my: 3 }} />
                <MDBox
                  p={2}
                  mb={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(52, 71, 103, 0.04)",
                    border: "1px solid rgba(52,71,103,0.1)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Filter by type"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": { minHeight: "44px" },
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="video">Video</MenuItem>
                        <MenuItem value="text">Text</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Filter by status"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": { minHeight: "44px" },
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6} display="flex" alignItems="center">
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Checkbox
                          checked={allVisibleSelected}
                          indeterminate={selectedContentIds.length > 0 && !allVisibleSelected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setSelectedContentIds((prev) => {
                              if (!checked) {
                                return prev.filter((id) => !items.some((item) => item.id === id));
                              }
                              return [...new Set([...prev, ...items.map((item) => item.id)])];
                            });
                          }}
                          size="small"
                        />
                        <MDTypography variant="button" color="text">
                          Showing {items.length} of {totalContents} content items
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox mt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
                <Menu
                  anchorEl={actionAnchorEl}
                  open={Boolean(actionAnchorEl)}
                  onClose={() => {
                    setActionAnchorEl(null);
                    setSelectedActionItem(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  {String(selectedActionItem?.status || "").toLowerCase() === "archived" ? (
                    <MenuItem
                      onClick={async () => {
                        if (!selectedActionItem) return;
                        await patchContentStatusApi(selectedActionItem.id, "published");
                        setReloadKey((prev) => prev + 1);
                        setActionAnchorEl(null);
                        setSelectedActionItem(null);
                      }}
                    >
                      Publish
                    </MenuItem>
                  ) : null}
                  <MenuItem
                    onClick={() => {
                      if (!selectedActionItem) return;
                      setEditingId(selectedActionItem.id);
                      setTitle(selectedActionItem.title);
                      setContentType(selectedActionItem.type);
                      setContentStatus(selectedActionItem.status || "published");
                      setCategory(selectedActionItem.category);
                      setShowAddPanel(true);
                      if (selectedActionItem.type === "video") {
                        setContentBody("");
                      } else {
                        setContentBody(selectedActionItem.body);
                      }
                      setUploadedFileName("");
                      setUploadedFile(null);
                      setActionAnchorEl(null);
                      setSelectedActionItem(null);
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionItem) return;
                      await moveContentApi(selectedActionItem.id, "up");
                      setReloadKey((prev) => prev + 1);
                      setActionAnchorEl(null);
                      setSelectedActionItem(null);
                    }}
                  >
                    Move Up
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionItem) return;
                      await moveContentApi(selectedActionItem.id, "down");
                      setReloadKey((prev) => prev + 1);
                      setActionAnchorEl(null);
                      setSelectedActionItem(null);
                    }}
                  >
                    Move Down
                  </MenuItem>
                  {String(selectedActionItem?.status || "").toLowerCase() === "published" ? (
                    <MenuItem
                      onClick={async () => {
                        if (!selectedActionItem) return;
                        await patchContentStatusApi(selectedActionItem.id, "archived");
                        setReloadKey((prev) => prev + 1);
                        setActionAnchorEl(null);
                        setSelectedActionItem(null);
                      }}
                    >
                      Archive
                    </MenuItem>
                  ) : null}
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionItem) return;
                      if (!window.confirm("Do you want to delete this content?")) return;
                      await deleteContentApi(selectedActionItem.id);
                      setReloadKey((prev) => prev + 1);
                      setActionAnchorEl(null);
                      setSelectedActionItem(null);
                    }}
                    sx={{ color: "error.main" }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={showPreviewDialog}
        onClose={() => {
          setShowPreviewDialog(false);
          setPreviewItem(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {previewItem?.title || "Content Preview"}
          <IconButton
            size="small"
            onClick={() => {
              setShowPreviewDialog(false);
              setPreviewItem(null);
            }}
            sx={{ color: "#344767" }}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isPreviewLoading ? (
            <MDTypography variant="button" color="text">
              Loading content...
            </MDTypography>
          ) : previewItem ? (
            <MDBox>
              <MDBox mb={2} display="flex" gap={1} flexWrap="wrap">
                <MDBadge
                  badgeContent={previewItem.type}
                  color="info"
                  variant="gradient"
                  size="sm"
                />
                <MDBadge
                  badgeContent={previewItem.status}
                  color={
                    previewItem.status === "published"
                      ? "success"
                      : previewItem.status === "archived"
                      ? "error"
                      : "warning"
                  }
                  variant="gradient"
                  size="sm"
                />
                <MDBadge
                  badgeContent={previewItem.category}
                  color="dark"
                  variant="outlined"
                  size="sm"
                />
              </MDBox>
              {previewItem.type === "video" ? (
                previewItem.videoUrl ? (
                  <video
                    controls
                    style={{ width: "100%", borderRadius: "8px", backgroundColor: "#000" }}
                    src={previewItem.videoUrl}
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <MDTypography variant="button" color="text">
                    Video URL not available for this content.
                  </MDTypography>
                )
              ) : (
                <MDBox
                  p={2}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.04)", whiteSpace: "pre-wrap" }}
                >
                  <MDTypography variant="button" color="text">
                    {previewItem.text || "No text content available."}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          ) : (
            <MDTypography variant="button" color="text">
              Content detail not found.
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => {
              setShowPreviewDialog(false);
              setPreviewItem(null);
            }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={showAddPanel} onClose={resetForm} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {editingId ? "Edit Content" : "Add Content"}
          <IconButton size="small" onClick={resetForm} sx={{ color: "#344767" }}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" mb={2} display="block">
            Fill in the details to add onboarding videos and instructional text content.
            {!editingId
              ? ` Maximum ${MAX_VIDEOS} videos allowed (${totalVideos}/${MAX_VIDEOS} used).`
              : ""}
          </MDTypography>
          {formError ? (
            <MDBox mb={1.5}>
              <MDTypography variant="caption" color="error">
                {formError}
              </MDTypography>
            </MDBox>
          ) : null}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title"
                fullWidth
                variant="outlined"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ backgroundColor: "#fff", "& .MuiOutlinedInput-root": { minHeight: "44px" } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Type"
                fullWidth
                variant="outlined"
                value={contentType}
                onChange={(e) => {
                  setContentType(e.target.value);
                  setFormError("");
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ backgroundColor: "#fff", "& .MuiOutlinedInput-root": { minHeight: "44px" } }}
              >
                <MenuItem value="video" disabled={!editingId && totalVideos >= MAX_VIDEOS}>
                  Video
                  {!editingId && totalVideos >= MAX_VIDEOS ? " (limit reached)" : ""}
                </MenuItem>
                <MenuItem value="text">Text</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Status"
                fullWidth
                variant="outlined"
                value={contentStatus}
                onChange={(e) => setContentStatus(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ backgroundColor: "#fff", "& .MuiOutlinedInput-root": { minHeight: "44px" } }}
              >
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Category"
                fullWidth
                variant="outlined"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ backgroundColor: "#fff", "& .MuiOutlinedInput-root": { minHeight: "44px" } }}
              >
                <MenuItem value="Getting Started">Getting Started</MenuItem>
                <MenuItem value="Profile Setup">Profile Setup</MenuItem>
                <MenuItem value="Usage Tips">Usage Tips</MenuItem>
                <MenuItem value="Billing Help">Billing Help</MenuItem>
              </TextField>
            </Grid>
            {contentType === "video" ? (
              <Grid item xs={12} md={3}>
                <TextField
                  label="Video Source"
                  fullWidth
                  variant="outlined"
                  value="Video Media"
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-root": { minHeight: "44px" },
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {contentType === "video" ? (
                <MDBox
                  mt={0.5}
                  p={1.5}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.04)" }}
                >
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
                    <MDButton variant="outlined" component="label" color="dark">
                      Upload Video
                      <input
                        hidden
                        type="file"
                        accept="video/*"
                        disabled={!editingId && totalVideos >= MAX_VIDEOS}
                        onChange={(e) => {
                          if (!editingId && totalVideos >= MAX_VIDEOS) {
                            setFormError(
                              `Maximum ${MAX_VIDEOS} videos allowed. Delete an existing video to add a new one.`
                            );
                            e.target.value = "";
                            return;
                          }
                          const file = e.target.files?.[0];
                          setUploadedFileName(file ? file.name : "");
                          setUploadedFile(file || null);
                        }}
                      />
                    </MDButton>
                    {uploadedFileName ? <Chip label={uploadedFileName} size="small" /> : null}
                  </Stack>
                </MDBox>
              ) : (
                <TextField
                  label="Instructional Text"
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  value={contentBody}
                  onChange={(e) => setContentBody(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: "#fff", mt: 0.5 }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <MDButton variant="outlined" color="dark" onClick={resetForm}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSaveContent}
            disabled={!editingId && contentType === "video" && totalVideos >= MAX_VIDEOS}
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
          >
            {editingId ? "Update Content" : "Save Content"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ContentManagement;
